import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Wallet, Save, CreditCard, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { affiliateApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import AffiliateNav from "./AffiliateNav";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const KIND_LABEL = { first_purchase: "First purchase", credits: "AI credits", residual: "Residual renewal" };

export default function AffiliatePayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthed, setUnauthed] = useState(false);
  const [stripePayoutEmail, setStripePayoutEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    affiliateApi.dashboard()
      .then((d) => {
        setData(d);
        setStripePayoutEmail(d.affiliate.stripePayoutEmail || "");
      })
      .catch(() => setUnauthed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (unauthed) return <Navigate to="/affiliate/login" replace />;
  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0E17]"><Loader2 className="h-7 w-7 text-amber-400 animate-spin" /></div>;
  }

  const { affiliate, commissionLogs } = data;
  const paidLogs = (commissionLogs || []).filter((l) => l.status === "paid");

  const connectStripe = () => {
    window.location.href = process.env.REACT_APP_AFFILIATE_STRIPE_CONNECT_URL;
  };

  const savePayout = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await affiliateApi.updatePayout({ stripePayoutEmail: stripePayoutEmail.trim() });
      setData((d) => ({ ...d, affiliate: updated }));
      toast.success("Payout details saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save payout details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E17] text-slate-200" data-testid="affiliate-payouts-page">
      <AffiliateNav affiliateName={affiliate.name} />

      <div className="px-6 py-6 max-w-4xl mx-auto space-y-6">
        {/* Pending / paid summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] p-4" data-testid="affiliate-payouts-pending">
            <div className="text-[11px] uppercase tracking-wider text-amber-300/80 font-semibold mb-1">Pending payout</div>
            <div className="text-[22px] font-bold text-white tabular-nums">{money(affiliate.totalCommissionPending)}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="affiliate-payouts-paid">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Total paid out</div>
            <div className="text-[22px] font-bold text-emerald-300 tabular-nums">{money(affiliate.totalCommissionPaid)}</div>
          </div>
        </div>

        {/* Payout destination */}
        <form onSubmit={savePayout} className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4" data-testid="affiliate-payout-form">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-amber-400" />
            <h3 className="text-[14px] font-bold text-slate-100">Payout destination</h3>
          </div>
          <p className="text-[12.5px] text-slate-500 -mt-1">
            Tell us where to send your commissions. We currently pay out manually via Stripe — this just keeps your details on file for the platform team.
          </p>

          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="affiliate-stripe-connect-section">
            <p className="text-[12.5px] text-slate-400 leading-relaxed">
              To receive commission payments, connect your Stripe account. Click the button below and you will be guided through Stripe's secure onboarding process. You will need to provide your legal name, tax information, and bank account details directly to Stripe. Once connected, your commission payments will be sent directly to your bank account.
            </p>
            {affiliate.stripeConnected ? (
              <div className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[13px] font-bold" data-testid="affiliate-stripe-connected-status">
                <ShieldCheck className="h-4 w-4" /> Stripe Account Connected
              </div>
            ) : (
              <button
                type="button"
                onClick={connectStripe}
                data-testid="affiliate-stripe-connect-btn"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-[#0A0E17] text-[13px] font-bold"
              >
                <CreditCard className="h-4 w-4" /> Connect with Stripe to Get Paid
              </button>
            )}
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Stripe / PayPal email <span className="text-slate-500 font-normal">(optional, if you don't have an account ID)</span></label>
            <div className="relative">
              <Mail className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={stripePayoutEmail}
                onChange={(e) => setStripePayoutEmail(e.target.value)}
                placeholder="you@partner.com"
                data-testid="affiliate-stripe-email"
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/50"
              />
            </div>
          </div>

          <button
            type="submit" disabled={saving}
            data-testid="affiliate-payout-save"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-[#0A0E17] text-[13px] font-bold disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save payout details
          </button>

          {(affiliate.stripeConnected || affiliate.stripePayoutEmail) && (
            <div className="flex items-center gap-1.5 text-[12px] text-emerald-300" data-testid="affiliate-payout-on-file">
              <ShieldCheck className="h-3.5 w-3.5" /> Payout details on file
            </div>
          )}
        </form>

        {/* Payout history */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-3.5 border-b border-white/10"><h3 className="text-[14px] font-bold text-slate-100">Payout history</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px] min-w-[560px]">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
                <tr>
                  <th className="px-5 py-2.5 font-semibold">Paid on</th><th className="px-3 py-2.5 font-semibold">Business</th>
                  <th className="px-3 py-2.5 font-semibold">Type</th><th className="px-5 py-2.5 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {paidLogs.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">No payouts sent yet.</td></tr>
                ) : paidLogs.map((l) => (
                  <tr key={l.id} data-testid={`affiliate-payout-${l.id}`}>
                    <td className="px-5 py-3 text-slate-400">{fmtDate(l.paidAt)}</td>
                    <td className="px-3 py-3 text-slate-200">{l.companyName}</td>
                    <td className="px-3 py-3 text-slate-300">{KIND_LABEL[l.kind] || l.kind}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(l.commissionAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
